import { addDays, subMonths } from 'date-fns'

import {
  fromCalendarDate,
  toCalendarDate,
  type CalendarDateRange
} from './calendar-date.ts'
import {
  calendarMonth,
  monthToDate,
  previousCalendarMonth,
  previousDay,
  rollingDays
} from './reporting-period.ts'
import { zonedToday } from './time-zone.ts'

const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december'
]
const MONTH = MONTHS.join('|')
const PHRASE = new RegExp(
  `\\b(?:since (?:\\d{4}-\\d{2}-\\d{2}|(?:${MONTH}) \\d{1,2}(?:,? \\d{4})?)|last [+-]?\\d+(?:\\.\\d+)? (?:days?|weeks?|months?)|this month|last month|today|yesterday|in (?:${MONTH})|(?:${MONTH}))\\b`,
  'gi'
)

/** A recognized range and the question with its range phrase removed. */
export interface SearchDateRange {
  topic: string
  label: string | null
  range: CalendarDateRange | null
}

function sinceDay(text: string, reference: Date): Date | undefined {
  const iso = fromCalendarDate(text)
  if (iso) return iso
  const match = new RegExp(`^(${MONTH}) (\\d{1,2})(?:,? (\\d{4}))?$`, 'i').exec(
    text
  )
  if (!match) return undefined
  const month = MONTHS.indexOf(match[1].toLowerCase()) + 1
  const year = match[3]
    ? Number(match[3])
    : reference.getFullYear() - (month > reference.getMonth() + 1 ? 1 : 0)
  return fromCalendarDate(
    `${year}-${String(month).padStart(2, '0')}-${match[2].padStart(2, '0')}`
  )
}

function rangeFor(phrase: string, reference: Date): CalendarDateRange | null {
  if (phrase === 'today') return rollingDays(1, reference)
  if (phrase === 'yesterday') return previousDay(reference)
  if (phrase === 'this month') return monthToDate(reference)
  if (phrase === 'last month') return previousCalendarMonth(reference)
  if (phrase.startsWith('since ')) {
    const start = sinceDay(phrase.slice(6), reference)
    return start && start <= reference
      ? { startDate: toCalendarDate(start), endDate: toCalendarDate(reference) }
      : null
  }
  const rolling = /^last (\d+) (days?|weeks?|months?)$/.exec(phrase)
  if (rolling) {
    const count = Number(rolling[1])
    const unit = rolling[2]
    if (
      !Number.isSafeInteger(count) ||
      count < 1 ||
      count > (unit.startsWith('month') ? 1200 : 36600)
    )
      return null
    return unit.startsWith('month')
      ? {
          startDate: toCalendarDate(addDays(subMonths(reference, count), 1)),
          endDate: toCalendarDate(reference)
        }
      : rollingDays(count * (unit.startsWith('week') ? 7 : 1), reference)
  }
  const month = MONTHS.indexOf(phrase.replace(/^in /, ''))
  if (month === -1) return null
  const year = reference.getFullYear() - (month > reference.getMonth() ? 1 : 0)
  return month === reference.getMonth()
    ? monthToDate(reference)
    : calendarMonth(year, month)
}

/** Parse one range against the reader's civil day. Invalid or ambiguous ranges retain the original question. */
export function searchDateRange(
  query: string,
  { timeZone, now }: { timeZone: string; now: Date }
): SearchDateRange {
  const unchanged = { topic: query, label: null, range: null }
  const matches = [...query.matchAll(PHRASE)]
  if (matches.length !== 1) return unchanged
  const match = matches[0]
  const before = query.slice(0, match.index)
  const after = query.slice(match.index + match[0].length)
  const monthContext =
    /(?:^|\s)(?:in|for|during|from|of|by|until)(?:\s+that)?\s*$/i.test(
      before
    ) ||
    after.trim() === '' ||
    (before.trim() === '' &&
      !/^\s+(?:i|we|you|they|he|she|it|be|not|have|apply|owe)\b/i.test(after))
  const modalMay = /^may$/i.test(match[0]) && !monthContext
  if (modalMay || /\bsince\s*$/i.test(query.slice(0, match.index)))
    return unchanged
  const label = match[0].toLowerCase()
  const range = rangeFor(label, zonedToday(timeZone, now))
  if (!range) return unchanged
  return {
    topic:
      `${query.slice(0, match.index)} ${query.slice(match.index + match[0].length)}`
        .trim()
        .replace(/\s+/g, ' '),
    label,
    range
  }
}
